import { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import CyberCubLogo from '../../components/CyberCubLogo.jsx';
import Tutor from '../../components/Tutor.jsx';
import { useFinished } from '../../lib/practical.js';

/**
 * Practical site for "Windows Basics".
 *
 * Three sections that unlock in order:
 *
 *   The desktop  - a drawn Windows desktop with the parts unlabelled.
 *                  Click the one being asked for.
 *   Read a path  - six real paths, pulled apart. Which drive, whose
 *                  folder, and what kind of file is it really.
 *   Who can?     - administrator against standard, and the prompt that
 *                  sits between them. The section the lesson is for.
 *
 * The desktop is drawn rather than screenshotted on purpose: a
 * screenshot dates the moment Windows changes its wallpaper, and a
 * child running a different version would be looking for something that
 * is not there. The shapes are what matter.
 */

const STORE_KEY = 'cybercub.lab.windows-basics';

/* ------------------------------------------------------------------ */
/* 1. find your way around the desktop                                */
/* ------------------------------------------------------------------ */

/*
 * Hotspots, in percentages of the drawn desktop so they stay put at
 * whatever size the picture ends up. Each sits over a different part,
 * with no overlap - the taskbar's own region is a bare stretch of it,
 * away from the buttons that live there.
 */
const SPOTS = [
  {
    id: 'start',
    name: 'Start',
    x: 2.5, y: 90.5, w: 6.5, h: 9,
    ask: 'Where do you click to find a program that is not on the desktop?',
    why: 'Start opens the menu of everything installed. Typing there searches the whole machine.',
  },
  {
    id: 'taskbar',
    name: 'Taskbar',
    x: 46, y: 90.5, w: 34, h: 9,
    ask: 'Which strip along the bottom shows you what is open right now?',
    why: 'The taskbar. Every open window has a button on it, and pinned programs sit there too.',
  },
  {
    id: 'explorer',
    name: 'File Explorer',
    x: 25, y: 90.5, w: 6.5, h: 9,
    ask: 'Which icon on the taskbar opens the files and folders on this machine?',
    why: 'File Explorer - the folder icon. It is how you reach anything stored on the disk.',
  },
  {
    id: 'tray',
    name: 'Notification area',
    x: 82, y: 90.5, w: 16, h: 9,
    ask: 'Where do the clock, the network and the volume live?',
    why: 'The notification area, bottom right. Programs running quietly in the background sit here too.',
  },
  {
    id: 'icons',
    name: 'Desktop icons',
    x: 1.5, y: 3, w: 14, h: 52,
    ask: 'Where are the shortcuts you can keep on the background itself?',
    why: 'The desktop. Handy, and not where anything is really stored - most of these are only shortcuts.',
  },
];

/* What File Explorer is showing. Ordinary names on purpose: a child
   should recognise this as their own machine, not a demonstration. */
const FILES = [
  { icon: 'doc', name: 'homework.docx', date: '12/09/2026', size: '18 KB' },
  { icon: 'img', name: 'birthday.jpg', date: '09/09/2026', size: '2.1 MB' },
  { icon: 'folder', name: 'School project', date: '04/09/2026', size: '' },
  { icon: 'pdf', name: 'reading list.pdf', date: '01/09/2026', size: '340 KB' },
  { icon: 'doc', name: 'story draft.docx', date: '28/08/2026', size: '24 KB' },
];

const SIDEBAR = ['Quick access', 'Desktop', 'Downloads', 'Documents', 'Pictures', 'This PC'];

/** The small pictograms in the file list and on the desktop. */
function FileGlyph({ kind, x, y }) {
  if (kind === 'folder') {
    return (
      <g transform={`translate(${x} ${y})`}>
        <path className="win-g-folder" d="M0,0.6 h1.8 l0.5,0.7 h2.4 v3.1 h-4.7 z" />
      </g>
    );
  }
  if (kind === 'img') {
    return (
      <g transform={`translate(${x} ${y})`}>
        <rect className="win-g-img" x="0" y="0.6" width="4.6" height="3.8" rx="0.4" />
        <circle className="win-g-img-sun" cx="1.4" cy="1.8" r="0.5" />
        <path className="win-g-img-hill" d="M0.3,4.2 L1.8,2.5 L2.9,3.5 L3.8,2.8 L4.3,4.2 Z" />
      </g>
    );
  }
  const tone = kind === 'pdf' ? 'win-g-pdf' : 'win-g-doc';
  return (
    <g transform={`translate(${x} ${y})`}>
      <path className={tone} d="M0.4,0.5 h2.7 l1.3,1.3 v2.7 h-4 z" />
      <path className="win-g-fold" d="M3.1,0.5 v1.3 h1.3 z" />
    </g>
  );
}

/**
 * A Windows desktop, drawn.
 *
 * Not a screenshot, deliberately. A screenshot dates the moment Windows
 * changes its wallpaper, and a child on a different version would be
 * hunting for something that is not there. What has to be right is the
 * shape of the thing: a bar along the bottom with Start at one end and
 * the clock at the other, icons on the background, and a File Explorer
 * window with an address bar reading a path they have just been taught
 * to read.
 */
function Desktop({ askingFor, found, onPick, wrong }) {
  return (
    <div className="win-desktop">
      <svg viewBox="0 0 160 100" aria-hidden="true">
        <defs>
          <linearGradient id="win-wall" x1="0" y1="0" x2="0.4" y2="1">
            <stop offset="0%" stopColor="#0f3f6b" />
            <stop offset="55%" stopColor="#123a63" />
            <stop offset="100%" stopColor="#0a1f3a" />
          </linearGradient>
          <radialGradient id="win-bloom" cx="0.62" cy="0.34" r="0.55">
            <stop offset="0%" stopColor="#4aa3e8" stopOpacity="0.5" />
            <stop offset="100%" stopColor="#4aa3e8" stopOpacity="0" />
          </radialGradient>
        </defs>

        {/* ---------- wallpaper ---------- */}
        <rect x="0" y="0" width="160" height="100" fill="url(#win-wall)" />
        <ellipse cx="99" cy="34" rx="88" ry="55" fill="url(#win-bloom)" />

        {/* ---------- desktop icons ---------- */}
        {[
          { kind: 'pc', label: 'This PC' },
          { kind: 'folder', label: 'My work' },
          { kind: 'bin', label: 'Recycle Bin' },
        ].map((item, i) => {
          const y = 6 + i * 17;
          return (
            <g key={item.label} transform={`translate(5 ${y})`}>
              {item.kind === 'pc' && (
                <>
                  <rect className="win-g-pc" x="0.6" y="0.5" width="7.8" height="5.4" rx="0.6" />
                  <rect className="win-g-pc-screen" x="1.4" y="1.3" width="6.2" height="3.6" rx="0.3" />
                  <rect className="win-g-pc" x="3" y="6.2" width="3" height="1" rx="0.3" />
                </>
              )}
              {item.kind === 'folder' && (
                <path className="win-g-folder" d="M0.6,1.2 h3.2 l0.9,1.2 h4.2 v4.6 h-8.3 z" />
              )}
              {item.kind === 'bin' && (
                <>
                  <path className="win-g-bin" d="M2,1.8 h5 l-0.6,5.2 h-3.8 z" />
                  <rect className="win-g-bin" x="1.4" y="0.9" width="6.2" height="0.9" rx="0.4" />
                </>
              )}
              <text className="win-icon-label" x="4.5" y="9.6">{item.label}</text>
            </g>
          );
        })}

        {/* ---------- File Explorer ---------- */}
        <g>
          <rect className="win-win" x="34" y="8" width="112" height="76" rx="1.6" />

          {/* title bar */}
          <rect className="win-titlebar" x="34" y="8" width="112" height="6.4" rx="1.6" />
          <rect className="win-titlebar" x="34" y="12" width="112" height="2.4" />
          <path className="win-g-folder" d="M36.6,10 h1.6 l0.4,0.6 h2.2 v2.6 h-4.2 z" />
          <text className="win-win-title" x="42.6" y="12.6">Documents</text>
          <text className="win-win-btn" x="131" y="12.6">—</text>
          <text className="win-win-btn" x="137" y="12.6">▢</text>
          <text className="win-win-btn is-close" x="142.6" y="12.6">✕</text>

          {/* toolbar and address bar */}
          <rect className="win-toolbar" x="34" y="14.4" width="112" height="7.2" />
          <text className="win-nav-arrow" x="37.4" y="19">←</text>
          <text className="win-nav-arrow is-dim" x="42.4" y="19">→</text>
          <text className="win-nav-arrow" x="47.4" y="19">↑</text>
          <rect className="win-address" x="52" y="15.6" width="72" height="4.6" rx="0.8" />
          <text className="win-address-text" x="54" y="18.9">C:\Users\Aarav\Documents</text>
          <rect className="win-search" x="126" y="15.6" width="18" height="4.6" rx="0.8" />
          <text className="win-search-text" x="128" y="18.9">Search</text>

          {/* sidebar */}
          <rect className="win-sidebar" x="34" y="21.6" width="30" height="62.4" />
          {SIDEBAR.map((item, i) => (
            <g key={item}>
              {item === 'Documents' && (
                <rect className="win-side-sel" x="34.6" y={23.4 + i * 6.4} width="28.8" height="5.4" rx="0.7" />
              )}
              <path
                className={item === 'This PC' ? 'win-g-pc-small' : 'win-g-folder-small'}
                d={`M37,${24.8 + i * 6.4} h1.5 l0.4,0.5 h2 v2.2 h-3.9 z`}
              />
              <text
                className={`win-side-text ${item === 'Documents' ? 'is-sel' : ''}`}
                x="42.4"
                y={27.6 + i * 6.4}
              >
                {item}
              </text>
            </g>
          ))}

          {/* column headings */}
          <rect className="win-cols" x="64" y="21.6" width="82" height="5.4" />
          <text className="win-col-text" x="70" y="25.4">Name</text>
          <text className="win-col-text" x="108" y="25.4">Date modified</text>
          <text className="win-col-text" x="134" y="25.4">Size</text>

          {/* files */}
          {FILES.map((f, i) => {
            const y = 27 + i * 7.6;
            return (
              <g key={f.name}>
                {i === 0 && <rect className="win-row-sel" x="64.6" y={y + 0.5} width="80.8" height="6.6" rx="0.7" />}
                <FileGlyph kind={f.icon} x={66.4} y={y + 1} />
                <text className="win-file-name" x="72.4" y={y + 4.9}>{f.name}</text>
                <text className="win-file-meta" x="108" y={y + 4.9}>{f.date}</text>
                <text className="win-file-meta" x="134" y={y + 4.9}>{f.size}</text>
              </g>
            );
          })}

          {/* status bar */}
          <rect className="win-status" x="34" y="78.6" width="112" height="5.4" />
          <text className="win-status-text" x="36.4" y="82.4">5 items</text>
          <text className="win-status-text" x="52" y="82.4">1 item selected · 18 KB</text>
        </g>

        {/* ---------- taskbar ---------- */}
        <rect className="win-taskbar" x="0" y="90" width="160" height="10" />

        {/* start: the four-pane logo */}
        <g>
          <rect className="win-logo" x="5.2" y="93.4" width="1.6" height="1.6" />
          <rect className="win-logo" x="7.2" y="93.4" width="1.6" height="1.6" />
          <rect className="win-logo" x="5.2" y="95.4" width="1.6" height="1.6" />
          <rect className="win-logo" x="7.2" y="95.4" width="1.6" height="1.6" />
        </g>

        {/* search box */}
        <rect className="win-tb-search" x="12" y="92.8" width="24" height="4.6" rx="2.3" />
        <circle className="win-tb-magnifier" cx="14.6" cy="95.1" r="1" />
        <line className="win-tb-magnifier-handle" x1="15.3" y1="95.8" x2="16" y2="96.5" />
        <text className="win-tb-search-text" x="17.6" y="96.4">Search</text>

        {/* pinned apps - the folder one is File Explorer */}
        <path className="win-g-folder" d="M41,93.6 h2.2 l0.6,0.8 h3 v3.4 h-5.8 z" />
        <rect className="win-tb-app is-edge" x="51" y="93.2" width="4.4" height="4.4" rx="2.2" />
        <rect className="win-tb-app is-mail" x="58" y="93.4" width="4.6" height="3.8" rx="0.6" />

        {/* tray */}
        <path className="win-tray-wifi" d="M134,96.2 a3.4,3.4 0 0 1 4.6,0 M135.2,97.2 a1.8,1.8 0 0 1 2.2,0" />
        <path className="win-tray-speaker" d="M141.6,94.6 h1 l1.4,-1.2 v4.4 l-1.4,-1.2 h-1 z" />
        <text className="win-tray-clock" x="148.4" y="94.9">14:32</text>
        <text className="win-tray-clock" x="148.4" y="98.2">12/09/2026</text>
      </svg>

      {SPOTS.map((s) => {
        const done = found.includes(s.id);
        const isWrong = wrong === s.id;
        return (
          <button
            key={s.id}
            type="button"
            className={`win-spot ${done ? 'is-found' : ''} ${isWrong ? 'is-wrong' : ''}`}
            style={{ left: `${s.x}%`, top: `${s.y}%`, width: `${s.w}%`, height: `${s.h}%` }}
            onClick={() => onPick(s.id)}
            aria-label={done ? `${s.name}, found` : 'Unlabelled part of the desktop'}
            disabled={done || !askingFor}
          >
            {done && <span className="win-spot-tag">{s.name}</span>}
          </button>
        );
      })}
    </div>
  );
}

/* ------------------------------------------------------------------ */
/* 2. read a path                                                     */
/* ------------------------------------------------------------------ */

const PATHS = [
  {
    id: 'p1',
    path: 'C:\\Users\\Aarav\\Documents\\homework.docx',
    q: 'Whose folder is this file in?',
    options: ['Nobody — it is a system file', "Aarav's", 'Everyone on the machine', 'The administrator'],
    answer: 1,
    why: 'Everything under C:\\Users\\<name> belongs to that person, and other accounts cannot simply walk into it.',
  },
  {
    id: 'p2',
    path: 'D:\\holiday\\beach.jpg',
    q: 'What does the D: tell you?',
    options: [
      'It is a document',
      'It is on a different drive from Windows — often a USB stick or second disk',
      'It is downloaded',
      'It is a shortcut',
    ],
    answer: 1,
    why: 'Windows itself lives on C:. Another letter means another drive entirely.',
  },
  {
    id: 'p3',
    path: 'C:\\Users\\Meera\\Downloads\\invoice.pdf.exe',
    q: 'What kind of file is this really?',
    options: ['A PDF document', 'A program that will run', 'A picture', 'A folder'],
    answer: 1,
    why: 'Only the last extension counts, and it is .exe. The .pdf in the middle is part of the name, put there to be believed.',
  },
  {
    id: 'p4',
    path: 'C:\\Windows\\System32\\',
    q: 'Why should you leave this folder alone?',
    options: [
      'It is empty',
      'It holds the files Windows itself needs to run',
      'It is where downloads go',
      'It belongs to another user',
    ],
    answer: 1,
    why: 'This is the operating system itself. It is also exactly where something nasty would like to be, which is why changing it needs administrator rights.',
  },
  {
    id: 'p5',
    path: 'C:\\Users\\Aarav\\Desktop\\game — shortcut.lnk',
    q: 'What is a .lnk file?',
    options: [
      'The game itself',
      'A shortcut — a signpost to a file kept somewhere else',
      'A broken file',
      'A picture of the game',
    ],
    answer: 1,
    why: 'Most desktop icons are shortcuts. Deleting one removes the signpost, not the thing it points at.',
  },
  {
    id: 'p6',
    path: 'C:\\Users\\Meera\\Downloads\\photo.jpg',
    q: 'File Explorer shows this as just "photo". Why?',
    options: [
      'The file is broken',
      'Windows hides known extensions by default',
      'Pictures never have extensions',
      'It was renamed',
    ],
    answer: 1,
    why: 'That setting ships switched on, and it is the reason the trick in the third path works so well. Turn extensions on.',
  },
];

/* ------------------------------------------------------------------ */
/* 3. who is allowed?                                                 */
/* ------------------------------------------------------------------ */

const RIGHTS = [
  {
    id: 'r1',
    icon: '🖼️',
    text: 'Aarav wants to change the desktop wallpaper on his own account.',
    options: ['Administrator needed', 'A standard account can do this'],
    answer: 1,
    why: 'It only changes his own settings. Nothing about the system itself moves.',
  },
  {
    id: 'r2',
    icon: '📦',
    text: 'A program wants to install itself for everybody who uses the computer.',
    options: ['Administrator needed', 'A standard account can do this'],
    answer: 0,
    why: 'Anything affecting every account changes the system, and that needs administrator rights.',
  },
  {
    id: 'r3',
    icon: '🗄️',
    text: 'Something wants to write a file into C:\\Windows\\System32.',
    options: ['Administrator needed', 'A standard account can do this'],
    answer: 0,
    why: 'That is the operating system itself. If a program you did not expect is asking for this, the answer is no.',
  },
  {
    id: 'r4',
    icon: '📄',
    text: 'Meera saves an essay into her own Documents folder.',
    options: ['Administrator needed', 'A standard account can do this'],
    answer: 1,
    why: 'Her folder, her file. This is what a standard account is for, and it is most of what anybody does all day.',
  },
];

/* ------------------------------------------------------------------ */

const load = () => {
  try { return JSON.parse(localStorage.getItem(STORE_KEY) ?? '{}'); } catch { return {}; }
};
const save = (state) => {
  try { localStorage.setItem(STORE_KEY, JSON.stringify(state)); } catch { /* private window */ }
};

export default function WindowsBasicsLab() {
  const saved = useMemo(load, []);

  const [tab, setTab] = useState('desktop');
  const [found, setFound] = useState(saved.found ?? []);
  const [wrong, setWrong] = useState(null);
  const [pathPicks, setPathPicks] = useState(saved.pathPicks ?? {});
  const [rightPicks, setRightPicks] = useState(saved.rightPicks ?? {});

  useEffect(() => {
    save({ found, pathPicks, rightPicks });
  }, [found, pathPicks, rightPicks]);

  useEffect(() => { document.title = 'Windows Basics · CyberCub Lab'; }, []);

  const asking = SPOTS.find((s) => !found.includes(s.id)) ?? null;
  const desktopDone = found.length === SPOTS.length;
  const pathsDone = PATHS.every((p) => pathPicks[p.id] === p.answer);
  const rightsDone = RIGHTS.every((r) => rightPicks[r.id] === r.answer);

  useFinished('/lab/windows-basics', rightsDone);

  const TABS = [
    { id: 'desktop', n: 1, label: 'Find your way around', locked: false },
    { id: 'paths', n: 2, label: 'Read a path', locked: !desktopDone },
    { id: 'rights', n: 3, label: 'Who is allowed?', locked: !pathsDone },
  ];

  const pick = (id) => {
    if (!asking) return;
    if (id === asking.id) {
      setWrong(null);
      setFound((f) => [...f, id]);
      return;
    }
    setWrong(id);
    setTimeout(() => setWrong(null), 700);
  };

  const back = (() => {
    if (tab === 'desktop') return null;
    if (tab === 'paths') return { label: '← Back to the desktop', act: () => setTab('desktop') };
    return { label: '← Back to the paths', act: () => setTab('paths') };
  })();

  return (
    <div className="lab">
      <header className="lab-top">
        <div className="row gap-sm">
          <CyberCubLogo size={38} />
          <div>
            <strong>Windows Basics</strong>
            <span className="lab-sub">Practical · Ethical Hacker, Module 2</span>
          </div>
        </div>
        <Link to="/learn/lesson/cf3-windows-basics" className="btn btn-sm btn-ghost">
          ← Back to the lesson
        </Link>
      </header>

      <nav className="lab-tabs" aria-label="Sections">
        {TABS.map((t) => (
          <button
            key={t.id}
            type="button"
            className={`lab-tab ${tab === t.id ? 'is-active' : ''} ${t.locked ? 'is-locked' : ''}`}
            onClick={() => !t.locked && setTab(t.id)}
            disabled={t.locked}
            aria-current={tab === t.id ? 'page' : undefined}
          >
            <span className="lab-tab-n">{t.n}</span>
            {t.label}
            {t.locked && <span aria-label="locked"> 🔒</span>}
          </button>
        ))}
      </nav>

      {/* ---------------- 1. the desktop ---------------- */}
      {tab === 'desktop' && (
        <section className="lab-section">
          <div className="place-head">
            <div>
              <h2>Find your way around</h2>
              <p className="lab-lead">
                A Windows desktop with nothing labelled. Read what is being asked for, then click it
                on the picture.
              </p>
            </div>
            <span className="pill">{found.length} / {SPOTS.length} found</span>
          </div>

          <Tutor
            avatar="cub-sky"
            say={
              desktopDone
                ? 'All five found. Every version of Windows moves these about a little, but the shapes stay put: a start button, a strip of what is open, a folder for your files, and a corner for the clock.'
                : asking
                  ? `Find this one: ${asking.ask}`
                  : 'Click the part of the desktop being asked for.'
            }
          />

          {asking && (
            <div className="win-ask">
              <span className="win-ask-n" aria-hidden="true">{found.length + 1}</span>
              <p>{asking.ask}</p>
            </div>
          )}

          <Desktop askingFor={asking} found={found} onPick={pick} wrong={wrong} />

          {found.length > 0 && (
            <ul className="win-learned">
              {SPOTS.filter((s) => found.includes(s.id)).map((s) => (
                <li key={s.id}><strong>{s.name}.</strong> {s.why}</li>
              ))}
            </ul>
          )}

          {desktopDone && (
            <div className="lab-done">
              <strong>All five.</strong> Now find out where the files behind those icons actually live.
              <button type="button" className="btn btn-sm" onClick={() => setTab('paths')}>
                Read a path →
              </button>
            </div>
          )}
        </section>
      )}

      {/* ---------------- 2. read a path ---------------- */}
      {tab === 'paths' && (
        <section className="lab-section">
          <div className="place-head">
            <div>
              <h2>Read a path</h2>
              <p className="lab-lead">
                Every file has an address: the drive, the folders, then the file. Six real ones —
                work out what each is telling you.
              </p>
            </div>
            <div className="row gap-sm">
              <button type="button" className="btn btn-sm btn-ghost" onClick={back.act}>
                {back.label}
              </button>
              <span className="pill">
                {PATHS.filter((p) => pathPicks[p.id] === p.answer).length} / {PATHS.length}
              </span>
            </div>
          </div>

          <Tutor
            avatar="cub-grape"
            say={
              pathsDone
                ? 'All six. The one worth carrying out of here is the third: only the last extension counts. Everything before it is just a name somebody chose.'
                : 'Read each path from the left: first the drive, then the folders, then the file itself. The bit after the last dot tells you what kind of file it really is — whatever the icon says.'
            }
          />

          {PATHS.map((p) => {
            const pick2 = pathPicks[p.id];
            const right = pick2 === p.answer;
            return (
              <div key={p.id} className={`lab-case ${right ? 'is-right' : pick2 !== undefined ? 'is-wrong' : ''}`}>
                <div className="grow">
                  <code className="win-path">{p.path}</code>
                  <p><strong>{p.q}</strong></p>
                  <div className="lab-choices">
                    {p.options.map((opt, oi) => (
                      <button
                        key={opt}
                        type="button"
                        className={`lab-choice ${pick2 === oi ? 'is-picked' : ''}`}
                        onClick={() => setPathPicks((prev) => ({ ...prev, [p.id]: oi }))}
                        disabled={right}
                      >
                        {opt}
                      </button>
                    ))}
                  </div>
                  {pick2 !== undefined && (
                    <p className={`lab-feedback ${right ? 'is-right' : 'is-wrong'}`}>
                      {right ? `✓ ${p.why}` : '✗ Not that one — read the path again from the left.'}
                    </p>
                  )}
                </div>
              </div>
            );
          })}

          {pathsDone && (
            <div className="lab-done">
              <strong>All six read.</strong> One question left, and it decides how much damage anything
              can do.
              <button type="button" className="btn btn-sm" onClick={() => setTab('rights')}>
                Who is allowed? →
              </button>
            </div>
          )}
        </section>
      )}

      {/* ---------------- 3. who is allowed ---------------- */}
      {tab === 'rights' && (
        <section className="lab-section">
          <div className="place-head">
            <div>
              <h2>Who is allowed?</h2>
              <p className="lab-lead">
                An administrator can change anything on the machine. A standard account can only
                change its own things. For each of these, decide which is needed.
              </p>
            </div>
            <div className="row gap-sm">
              <button type="button" className="btn btn-sm btn-ghost" onClick={back.act}>
                {back.label}
              </button>
              <span className="pill">
                {RIGHTS.filter((r) => rightPicks[r.id] === r.answer).length} / {RIGHTS.length}
              </span>
            </div>
          </div>

          <Tutor
            avatar="cub-coral"
            say={
              rightsDone
                ? 'That is the idea the whole lesson rests on. Anything that runs on your account can do exactly what your account can do — no more. Which is why doing everyday things on an administrator account hands away the one limit you had.'
                : 'Ask one question each time: does this change only my own things, or does it change the machine for everybody? The second one needs an administrator, and that is when the permission box appears.'
            }
          />

          {RIGHTS.map((r) => {
            const pick3 = rightPicks[r.id];
            const right = pick3 === r.answer;
            return (
              <div key={r.id} className={`lab-case ${right ? 'is-right' : pick3 !== undefined ? 'is-wrong' : ''}`}>
                <span className="lab-case-n" aria-hidden="true">{r.icon}</span>
                <div className="grow">
                  <p>{r.text}</p>
                  <div className="lab-choices">
                    {r.options.map((opt, oi) => (
                      <button
                        key={opt}
                        type="button"
                        className={`lab-choice ${pick3 === oi ? 'is-picked' : ''}`}
                        onClick={() => setRightPicks((prev) => ({ ...prev, [r.id]: oi }))}
                        disabled={right}
                      >
                        {oi === 0 ? '🛡️ ' : '👤 '}{opt}
                      </button>
                    ))}
                  </div>
                  {pick3 !== undefined && (
                    <p className={`lab-feedback ${right ? 'is-right' : 'is-wrong'}`}>
                      {right ? `✓ ${r.why}` : '✗ Not quite — does it change only this person’s things, or the whole machine?'}
                    </p>
                  )}
                </div>
              </div>
            );
          })}

          {rightsDone && (
            <div className="lab-done">
              <div>
                <strong>All four.</strong>
                <p style={{ margin: '0.3rem 0 0' }}>
                  Malware is only ever as powerful as the account that ran it. That is the whole
                  reason to do everyday things on a standard account, and to stop when a permission
                  box appears that you were not expecting.
                </p>
              </div>
              <Link to="/learn/lesson/cf3-windows-basics" className="btn btn-sm">
                ← Back to the lesson
              </Link>
            </div>
          )}
        </section>
      )}
    </div>
  );
}
